import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useCart } from "@/lib/CartContext";
import { LogoMark } from "./Logo";

export function PriceLine({ product, className = "" }) {
  if (product.price && product.price_verified === "yes") {
    return <span className={`font-display text-xl text-gold ${className}`} data-testid={`price-${product.product_id}`}>${product.price}</span>;
  }
  if (product.availability === "buy_external") {
    return <span className={`font-ui text-xs text-paper/50 ${className}`}>See current price on Amazon</span>;
  }
  return <span className={`font-ui text-xs text-paper/50 ${className}`}>Pricing announced at launch</span>;
}

export function NotifyForm({ product, compact = false }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const r = await api.post("/public/notify", { email, product_id: product.product_id, consent: true });
      setState("done");
      setMsg(r.data.message);
    } catch (err) {
      setState("error");
      setMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (state === "done") {
    return <p className="font-ui text-sm text-gold" data-testid={`notify-success-${product.product_id}`}>{msg}</p>;
  }
  return (
    <form onSubmit={submit} className={compact ? "flex gap-2" : "flex flex-col sm:flex-row gap-3"} data-testid={`notify-form-${product.product_id}`}>
      <label htmlFor={`notify-${product.product_id}`} className="sr-only">Email for launch notification</label>
      <input id={`notify-${product.product_id}`} type="email" required value={email}
        onChange={(e) => setEmail(e.target.value)} placeholder="Email for launch notice"
        className="flex-1 bg-transparent border border-paper/20 focus:border-crimson px-4 py-3 font-ui text-sm text-paper placeholder:text-paper/30 outline-none transition-colors duration-300"
        data-testid={`notify-email-${product.product_id}`} />
      <button type="submit" disabled={state === "sending"} data-testid={`notify-btn-${product.product_id}`}
        className="font-ui text-[11px] uppercase tracking-[0.2em] font-semibold border border-gold/50 text-gold px-6 py-3 rounded-full transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-50">
        {state === "sending" ? "…" : "Notify Me"}
      </button>
      {state === "error" && <p className="font-ui text-xs text-crimsonhi" role="alert">{msg}</p>}
    </form>
  );
}

export function ProductActions({ product }) {
  const { addItem } = useCart() || {};
  const [added, setAdded] = useState(false);

  if (product.availability === "buy_external" && product.external_url) {
    return (
      <a href={product.external_url} target="_blank" rel="noopener noreferrer"
        data-testid={`buy-${product.product_id}`}
        className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-7 py-3.5 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]">
        {product.external_label || "Buy on Amazon"} <ArrowUpRight size={14} />
      </a>
    );
  }
  if (product.availability === "purchasable") {
    return (
      <button
        onClick={() => { addItem(product); setAdded(true); setTimeout(() => setAdded(false), 1800); }}
        data-testid={`add-to-cart-${product.product_id}`}
        className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-7 py-3.5 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]">
        <ShoppingBag size={14} /> {added ? "Added to Cart" : "Add to Cart"}
      </button>
    );
  }
  return <NotifyForm product={product} />;
}

export function ProductTile({ product }) {
  if (product.image) {
    return <img src={assetUrl(product.image)} alt={product.title} className="w-full h-full object-cover" loading="lazy" />;
  }
  return (
    <div className="w-full h-full bg-gradient-to-b from-[#141414] to-[#0a0a0a] flex flex-col items-center justify-center gap-5 p-8 text-center">
      <LogoMark size={44} />
      <div>
        <p className="font-display text-paper/85 text-lg leading-snug tracking-wide">{product.title}</p>
        <span className="block w-8 h-px bg-crimson mx-auto my-3" />
        <p className="font-ui text-[9px] uppercase tracking-[0.35em] text-paper/35">Design in development</p>
      </div>
    </div>
  );
}

export default function ProductCard({ product, linkPrefix = "/merch/product" }) {
  const link = product.type === "book" ? "/bookstore/volume-one" : `${linkPrefix}/${product.product_id}`;
  return (
    <div className="group border border-paper/10 bg-coal transition-colors duration-500 hover:border-paper/25" data-testid={`product-card-${product.product_id}`}>
      <Link to={link} className="block aspect-[4/5] overflow-hidden" data-testid={`product-link-${product.product_id}`}>
        <ProductTile product={product} />
      </Link>
      <div className="p-6 border-t border-paper/10">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-paper leading-snug">{product.title}</h3>
            {product.subtitle && <p className="font-ui text-xs text-paper/45 mt-1">{product.subtitle}</p>}
          </div>
          {product.badge && (
            <span className={`shrink-0 font-ui text-[9px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${
              product.availability === "buy_external" || product.availability === "purchasable"
                ? "border-gold/50 text-gold" : "border-paper/20 text-paper/45"}`}
              data-testid={`badge-${product.product_id}`}>
              {product.badge}
            </span>
          )}
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <PriceLine product={product} />
          <ProductActions product={product} />
        </div>
      </div>
    </div>
  );
}
